import React from 'react';
import { 
  FaEllipsisH, 
  FaEdit, 
  FaTrash, 
  FaLink,
  FaBookmark, 
  FaRegBookmark, 
  FaFlag,
  FaGlobeAmericas,
  FaUserFriends,
  FaLock,
  FaRetweet
} from 'react-icons/fa';
import { formatPostDate } from '../utils/postUtils';
import styles from './PostHeader.module.css';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";

export default function PostHeader({
  post,
  isOwner,
  showMenu,
  onToggleMenu,
  onEdit,
  onDelete,
  onSave,
  onReport,
  isDeleting,
  isUpdating,
  isSaving,
}) {
  const isSharedPost = post?.type === 'SharedPost';

  // For SharedPost, the "author" shown in header is the person who shared it
  const displayUser = isSharedPost ? post.sharedBy : post.user;

  const handleAvatarError = (e) => {
    e.target.onerror = null;
    e.target.src = 'https://i.pravatar.cc/50';
  };

  const navigate = useNavigate();

  const handleGoToProfile = () => {
    if (!displayUser?.id) return;
    navigate(`/profile/${displayUser.id}`);
  };

  const getVisibilityInfo = (visibility) => {
    switch (visibility) {
      case 0:
        return { icon: <FaGlobeAmericas />, text: "Public" };
      case 1:
        return { icon: <FaUserFriends />, text: "Connections" };
      case 2:
        return { icon: <FaLock />, text: "Only Me" };
      default:
        return { icon: null, text: "" };
    }
  };
  const visibilityInfo = getVisibilityInfo(post.visibility);

  const handleCopyPostLink = async () => {
    try {
      const postUrl = `${window.location.origin}/post/${post.id}?type=${post.type}`;
      await navigator.clipboard.writeText(postUrl);
      toast.success("Post link copied!");
      onToggleMenu();
    } catch (error) {
      console.error("Copy failed", error);
      toast.error("Failed to copy link");
    }
  };

  const initials = displayUser?.name
    ?.split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className={styles.header}>
      {displayUser?.profilePhotoUrl ? (
         <img
        src={displayUser?.profilePhotoUrl}
        alt={displayUser?.name}
        className={styles.userPhoto}
        onError={handleAvatarError}
        onClick={handleGoToProfile}
      />
      ) : (
        <div className={styles.noPhoto} onClick={handleGoToProfile}>
          {initials || '?'}
        </div>
      )}
      

      <div className={styles.userInfo}>
        <h3 className={styles.userName} onClick={handleGoToProfile}>
          {displayUser?.name}
        </h3>

        {/* "shared a post" label for SharedPost */}
        {isSharedPost && (
          <span className={styles.sharedLabel}>
            <FaRetweet className={styles.sharedIcon} />
            shared a post
          </span>
        )}

        <div className={styles.metaInfo}>
          <span className={styles.postDate}>
            {formatPostDate(post.createdAt)}
          </span>
          <span className={styles.visibility}>
            {visibilityInfo.icon}
            {visibilityInfo.text}
          </span>
        </div>
      </div>

      <div className={styles.moreWrapper}>
        <button
          className={styles.moreBtn}
          onClick={onToggleMenu}
          disabled={isDeleting}
          aria-label="More options"
        >
          <FaEllipsisH />
        </button>

        {showMenu && (
          <>
            <div className={styles.dropdownMenu}>
              {isOwner ? (
                <>
                  {/* SharedPosts typically can't be edited (no caption editing for shares), 
                      but keep edit available if caption exists */}
                    <button
                      className={styles.dropdownItem}
                      onClick={onEdit}
                      disabled={isUpdating || isDeleting}
                    >
                      <FaEdit /> Edit Post
                    </button>
                  

                  <button
                    className={styles.dropdownItem}
                    onClick={handleCopyPostLink}
                  >
                    <FaLink /> Copy Post Link
                  </button>

                  <button
                    className={styles.dropdownItemDanger}
                    onClick={onDelete}
                    disabled={isDeleting || isUpdating}
                  >
                    <FaTrash /> {isDeleting ? 'Deleting...' : 'Delete Post'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={styles.dropdownItem}
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    {post.isSaved ? <FaBookmark /> : <FaRegBookmark />}
                    {' '}
                    {isSaving ? 'Processing...' : post.isSaved ? 'Unsave' : 'Save'}
                  </button>

                  <button
                    className={styles.dropdownItem}
                    onClick={handleCopyPostLink}
                  >
                    <FaLink /> Copy Post Link
                  </button>

                  <button
                    className={styles.dropdownItem}
                    onClick={onReport}
                  >
                    <FaFlag /> Report
                  </button>
                </>
              )}
            </div>
            <div
              className={styles.dropdownOverlay}
              onClick={onToggleMenu}
            />
          </>
        )}
      </div>
    </div>
  );
}